import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, firstValueFrom, BehaviorSubject, from, of, Subscriber, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Firestore } from '@angular/fire/firestore';
import { Timestamp, addDoc, collection, deleteDoc, doc, getDoc, getDocs, getFirestore, onSnapshot, orderBy, query, setDoc, updateDoc, where, QuerySnapshot, DocumentData, QueryDocumentSnapshot, DocumentSnapshot } from 'firebase/firestore';
import { AuthService } from './auth.service';
import { ChildAuthService } from './child-auth.service';
import { FirestoreUtilsService } from './firestore-utils.service';

// Define message location types
export type MessageLocation = 'inbox' | 'archive' | 'sent' | 'trash';

// Define an interface for the Firestore message document
interface FirestoreMessage extends DocumentData {
  id?: string;
  subject: string;
  body: string;
  senderId: string;
  senderName: string;
  recipientId: string;
  recipientName: string;
  parentId: string;
  actualSenderId?: string;
  timestamp: Timestamp;
  read: boolean;
  location: MessageLocation;
  ownerType?: 'sender' | 'recipient'; // Indicates whether this is the sender's or recipient's copy
}

// Define the Message interface used in the application
export interface Message {
  id?: string;
  subject: string;
  content: string;
  senderId: string;
  senderName?: string;
  recipientId: string;
  recipientName?: string;
  timestamp?: Date;
  read?: boolean;
  location?: MessageLocation;
  ownerType?: 'sender' | 'recipient';
}

@Injectable({
  providedIn: 'root'
})
export class MailService {
  private unreadCountSubject = new BehaviorSubject<number>(0);
  private unreadMessageCountsSubject = new BehaviorSubject<{ [userId: string]: number }>({});

  constructor(
    private firestore: Firestore,
    private ngZone: NgZone,
    private authService: AuthService,
    private childAuthService: ChildAuthService,
    private firestoreUtils: FirestoreUtilsService,
    private router: Router
  ) {
    this.initializeUnreadCounts();
  }

  private initializeUnreadCounts(): void {
    // Listen for auth changes to update message counts
    this.authService.currentUser$.subscribe(user => {
      if (user) {
        this.listenForUnreadMessages(user.uid);
      } else {
        this.unreadCountSubject.next(0);
        this.unreadMessageCountsSubject.next({});
      }
    });

    // Also listen for child auth changes
    this.childAuthService.getAuthState().subscribe(state => {
      if (state.isChildMode && state.loggedInChild) {
        this.listenForUnreadMessages(state.loggedInChild.id);
      }
    });
  }

  private listenForUnreadMessages(userId: string): void {
    // Wrap all Firestore operations in NgZone
    this.ngZone.runOutsideAngular(async () => {
      try {
        // Use our utility to create a parent-scoped query
        const messagesQuery = await this.firestoreUtils.createParentScopedQuery('messages');
        
        // Add read filter and timestamp ordering
        const q = query(
          messagesQuery,
          where('read', '==', false),
          orderBy('timestamp', 'desc')
        );

        console.log(`[MailService.listenForUnreadMessages] Setting up listener for unread messages for user ${userId}`);
        
        // Set up a real-time listener for unread messages
        const unsubscribe = onSnapshot(q, (snapshot: QuerySnapshot<DocumentData>) => {
          // Run UI updates back in Angular zone
          this.ngZone.run(() => {
            // Get all messages
            const allMessages = snapshot.docs.map(doc => {
              const data = doc.data();
              return { ...data, id: doc.id } as FirestoreMessage;
            });
            
            // Filter for messages where recipientId matches the provided userId
            const userMessages = allMessages.filter(msg => msg.recipientId === userId);
            const unreadCount = userMessages.length;
            
            console.log(`[MailService.listenForUnreadMessages] Found ${unreadCount} unread messages for ${userId}`);
            
            this.unreadCountSubject.next(unreadCount);

            // Update unread counts for all users (for family member cards)
            const allMessageCounts = { ...this.unreadMessageCountsSubject.value };
            allMessageCounts[userId] = unreadCount;
            this.unreadMessageCountsSubject.next(allMessageCounts);
          });
        }, error => {
          console.error(`[MailService.listenForUnreadMessages] Error listening for unread messages:`, error);
        });
        
        // TODO: Store the unsubscribe function and call it when appropriate (e.g., when component is destroyed)
        // This would prevent memory leaks, but we need a proper cleanup mechanism
      } catch (error) {
        console.error(`[MailService.listenForUnreadMessages] Error setting up listener:`, error);
      }
    });
  }

  // Get total unread count for the current user
  getUnreadCount(): Observable<number> {
    return this.unreadCountSubject.asObservable() as Observable<number>;
  }

  // Get unread counts per user ID (for family member cards)
  getUnreadMessageCounts(): Observable<{ [userId: string]: number }> {
    return this.unreadMessageCountsSubject.asObservable() as Observable<{ [userId: string]: number }>;
  }

  // Get all messages for the current user
  getMessages(): Observable<Message[]> {
    console.log('[MailService.getMessages] Starting message retrieval...');
    
    return new Observable<Message[]>((observer: Subscriber<Message[]>) => {
      let firestoreSub: any; // Will hold the Firestore unsubscribe function
      
      this.ngZone.runOutsideAngular(async () => {
        try {
          console.log('[MailService.getMessages] Running outside Angular zone');
          
          // Create a parent-scoped query using our utility service
          // This ensures we always query as the parent regardless of child mode
          const messagesQuery = await this.firestoreUtils.createParentScopedQuery('messages');
          
          // IMPORTANT: Don't modify the query further, the parent-scoped query already includes
          // the necessary parentId filter to comply with security rules
          // Just add the timestamp ordering to the parent-scoped query
          const finalQuery = query(messagesQuery, orderBy('timestamp', 'desc'));
          console.log('[MailService.getMessages] Created parent-scoped query with timestamp ordering');
          
          // Get child state for logging and client-side filtering
          const childState = await firstValueFrom(this.childAuthService.getAuthState());
          console.log('[MailService.getMessages] Child auth state:', JSON.stringify({
            isChildMode: childState.isChildMode,
            childId: childState.loggedInChild?.id || 'none'
          }));
          
          // Get the active child ID for filtering if in child mode
          const childId = await this.firestoreUtils.getActiveChildId();
          
          // Set up the snapshot listener
          firestoreSub = onSnapshot(
            finalQuery, 
            (snapshot: QuerySnapshot<DocumentData>) => {
              this.ngZone.run(() => {
                console.log(`[MailService.getMessages] Query returned ${snapshot.docs.length} messages`);
                
                const allMessages = snapshot.docs.map(doc => {
                  const data = doc.data();
                  return { ...data, id: doc.id } as FirestoreMessage;
                });
                
                // Apply appropriate filtering based on mode
                let filteredMessages: FirestoreMessage[];
                
                if (childState.isChildMode && childId && childId !== 'unknown') {
                  // In child mode, filter for messages related to this child
                  filteredMessages = allMessages.filter(data => {
                    // Messages sent by this child
                    const isSender = (data.senderId === childId || data.actualSenderId === childId);
                    // Messages received by this child
                    const isRecipient = data.recipientId === childId;
                    
                    // Apply ownership filtering to avoid duplicates:
                    // 1. For sent messages, only include those where ownerType is 'sender'
                    // 2. For received messages, only include those where ownerType is 'recipient'
                    if (isSender && isRecipient) {
                      // Edge case: self-messages - strictly filter by ownerType
                      // A message should only appear once with the correct ownerType
                      return data.ownerType === 'recipient'; // Prioritize recipient view for self-messages
                    } else if (isSender) {
                      return data.ownerType === 'sender'; // Only sender copies for sent messages
                    } else if (isRecipient) {
                      return data.ownerType === 'recipient'; // Only recipient copies for received messages
                    }
                    return false;
                  });
                  console.log(`[MailService.getMessages] After filtering for child ${childId}, found ${filteredMessages.length} messages`);
                } else {
                  // In parent mode or if childId is unknown, show messages for the parent
                  // Extract parentId from the query (first filter value is parentId)
                  const filters = (finalQuery as any)._query?.filters;
                  let parentId = null;
                  
                  // Extract the actual string value from the filter
                  if (filters && filters.length > 0) {
                    // The value might be an object with a stringValue property or directly a string
                    if (typeof filters[0].value === 'object' && filters[0].value !== null) {
                      parentId = filters[0].value.stringValue || filters[0].value.toString();
                      console.log('[MailService.getMessages] Extracted parentId from object:', parentId);
                    } else {
                      parentId = filters[0].value;
                      console.log('[MailService.getMessages] Using direct parentId value:', parentId);
                    }
                  }
                  
                  if (parentId) {
                    // We're going to be extra verbose and careful with this fix
                    console.log(`[MailService.getMessages] Filtering ${allMessages.length} messages for parentId: ${parentId}`);
                    
                    // Create a fresh array to collect the filtered messages
                    let matches = [];
                    
                    // Loop through all messages manually instead of using filter
                    // This gives us more control and better debugging
                    for (const data of allMessages) {
                      // Log the message being processed
                      console.log('[MailService.getMessages] Processing message:', JSON.stringify({
                        id: data.id,
                        ownerType: data.ownerType,
                        senderId: data.senderId, 
                        actualSenderId: data.actualSenderId,
                        recipientId: data.recipientId,
                        parentId: data.parentId,
                        subject: data.subject,
                        location: data.location
                      }));
                      
                      // First check - Is this message part of this parent's family?
                      if (data.parentId === parentId) {
                        console.log(`[MailService.getMessages] ✅ MATCH FOUND: Message ${data.id} has matching parentId ${parentId}`);
                        matches.push(data);
                      } else {
                        console.log(`[MailService.getMessages] ❌ NOT MATCHED: Message ${data.id} has wrong parentId ${data.parentId}`);
                      }
                    }
                    
                    console.log(`[MailService.getMessages] Found ${matches.length} matching messages out of ${allMessages.length} total`);
                    filteredMessages = matches;
                  } else {
                    filteredMessages = allMessages;
                  }
                  
                  console.log(`[MailService.getMessages] After filtering for parent, found ${filteredMessages.length} messages`);
                }
                
                // Map to message objects
                const messages = filteredMessages.map((data) => ({
                  id: data.id || '',
                  subject: data.subject,
                  content: data.body,
                  senderId: data.actualSenderId || data.senderId,
                  senderName: data.senderName,
                  recipientId: data.recipientId,
                  recipientName: data.recipientName,
                  timestamp: data.timestamp?.toDate() || new Date(),
                  read: data.read || false,
                  location: data.location || 'inbox',
                  ownerType: data.ownerType // Include the ownerType field
                }));
                
                console.log(`[MailService.getMessages] Returning ${messages.length} messages`);
                observer.next(messages);
              });
            },
            (error) => {
              this.ngZone.run(() => {
                console.error('[MailService.getMessages] ERROR fetching messages:', error);
                console.error('[MailService.getMessages] Error code:', error.code);
                console.error('[MailService.getMessages] Error message:', error.message);
                observer.error(error);
              });
            }
          );
        } catch (error) {
          this.ngZone.run(() => {
            console.error('[MailService.getMessages] Error setting up query:', error);
            observer.error(error);
          });
        }
      });
      
      // Return a teardown function that unsubscribes from the Firestore listener
      return () => {
        console.log('[MailService.getMessages] Unsubscribing from Firestore listener');
        if (firestoreSub) {
          firestoreSub(); // Firebase unsubscribe function is callable
        }
      };
    });
  }

  // Get a specific message by ID
  getMessage(id: string): Observable<Message | null> {
    console.log('[MailService.getMessage] Getting message by ID:', id);
    console.log('[MailService.getMessage] Current router URL:', this.router.url);
    
    return new Observable<Message | null>(observer => {
      console.log('[MailService.getMessage] Creating new observable');
      this.ngZone.runOutsideAngular(async () => {
        console.log('[MailService.getMessage] Running outside Angular zone');
        try {
          // Get child authentication state for later filtering
          const childState = await firstValueFrom(this.childAuthService.getAuthState());
          console.log('[MailService.getMessage] Child auth state:', JSON.stringify({
            isChildMode: childState.isChildMode,
            childId: childState.loggedInChild?.id || 'none'
          }));
          
          // Get active child ID if in child mode
          const childId = await this.firestoreUtils.getActiveChildId();
          
          // Get the message directly from Firestore
          console.log(`[MailService.getMessage] Fetching document from 'messages/${id}'`);
          const messageRef = doc(this.firestore, 'messages', id);
          const docSnap = await getDoc(messageRef);
          
          if (!docSnap.exists()) {
            console.error(`[MailService.getMessage] Message with ID ${id} not found`);
            this.ngZone.run(() => {
              console.log('[MailService.getMessage] Message not found');
              observer.next(null);
              observer.complete();
            });
            return;
          }
          
          // Get message data with proper typing
          const messageData = docSnap.data() as FirestoreMessage;
          console.log('[MailService.getMessage] Message data:', JSON.stringify({
            id: docSnap.id,
            parentId: messageData.parentId,
            senderId: messageData.senderId,
            actualSenderId: messageData.actualSenderId,
            recipientId: messageData.recipientId,
            subject: messageData.subject,
            read: messageData.read
          }));
          
          // Get the authenticated parent's ID to verify this message belongs to them
          const authUser = await this.authService.getCurrentUser();
          if (!authUser) {
            console.error('[MailService.getMessage] ERROR: No authenticated user found');
            throw new Error('No authenticated user found');
          }
          const parentId = authUser.uid;
          
          // Verify this message belongs to the parent
          console.log(`[MailService.getMessage] Checking parentId: document parentId=${messageData.parentId}, authenticated parentId=${parentId}`);
          if (messageData.parentId !== parentId) {
            console.error(`[MailService.getMessage] PERMISSION ERROR: Document parentId=${messageData.parentId} does not match authenticated parentId=${parentId}`);
            throw new Error('You do not have permission to view this message');
          }
          console.log('[MailService.getMessage] ParentId validation passed!');
          
          // Apply client-side filtering based on app mode
          let hasPermission = false;
          
          if (childState.isChildMode && childId) {
            // For child user, they should only see messages they sent or received
            hasPermission = 
              messageData.recipientId === childId || 
              messageData.actualSenderId === childId;
            
            console.log('[MailService.getMessage] Child user accessing message:', { 
              hasPermission,
              recipientId: messageData.recipientId, 
              actualSenderId: messageData.actualSenderId,
              childId
            });
          } else {
            // For parent user, they should see their own messages
            hasPermission = 
              messageData.recipientId === parentId || 
              messageData.actualSenderId === parentId ||
              messageData.senderId === parentId;
              
            console.log('[MailService.getMessage] Parent user accessing message:', { 
              hasPermission,
              recipientId: messageData.recipientId, 
              actualSenderId: messageData.actualSenderId, 
              parentId
            });
          }
          
          if (!hasPermission) {
            console.error('[MailService.getMessage] Client-side permission check failed');
            throw new Error('You do not have permission to view this message');
          }
          
          // Mark message as read if user is recipient
          const isRecipient = (
            (childState.isChildMode && childState.loggedInChild && messageData.recipientId === childState.loggedInChild.id) || 
            (!childState.isChildMode && messageData.recipientId === parentId)
          );
          
          if (isRecipient && !messageData.read) {
            console.log('Automatically marking message as read');
            this.markAsRead(id).catch(err => {
              console.error('Error marking message as read:', err);
            });
          }
          
          // Create the message object
          const message: Message = {
            id: docSnap.id,
            subject: messageData.subject,
            content: messageData.body, 
            senderId: messageData.actualSenderId || messageData.senderId,
            senderName: messageData.senderName,
            recipientId: messageData.recipientId,
            recipientName: messageData.recipientName,
            timestamp: messageData.timestamp?.toDate() || new Date(),
            read: messageData.read || false
          };
          
          this.ngZone.run(() => {
            console.log('Retrieved message from Firestore:', message);
            observer.next(message);
            observer.complete();
          });
        } catch (error) {
          this.ngZone.run(() => {
            console.error('[MailService.getMessage] Error getting message:', error);
            
            // Add more detailed error information for debugging
            if (error && typeof error === 'object' && 'code' in error) {
              const firebaseError: any = error;
              console.error('[MailService.getMessage] Firebase error details:', {
                code: firebaseError.code,
                message: firebaseError.message
              });
              
              // Create a more user-friendly error message based on the error type
              if (firebaseError.code === 'permission-denied') {
                observer.error(new Error('Permission denied: You do not have access to view this message.'));
              } else {
                observer.error(error);
              }
            } else {
              // For non-Firebase errors, still provide the original error
              observer.error(error);
            }
          });
        }
      });
      
      // Return cleanup function
      return () => {};
    });
  }

  // Mark a message as read
  async markAsRead(messageId: string): Promise<void> {
    console.log('[MailService.markAsRead] Marking message as read:', messageId);
    
    try {
      // Get child mode state and active child ID
      const childState = await firstValueFrom(this.childAuthService.getAuthState());
      console.log('[MailService.markAsRead] Child auth state:', JSON.stringify({
        isChildMode: childState.isChildMode,
        childId: childState.loggedInChild?.id || 'none'
      }));
      
      // Get active child ID using our utility
      const childId = await this.firestoreUtils.getActiveChildId();
      
      // Get authenticated user (always the parent in our architecture)
      const authUser = await this.authService.getCurrentUser();
      if (!authUser) {
        console.error('[MailService.markAsRead] ERROR: No authenticated user found');
        throw new Error('No authenticated user found');
      }
      
      // In parent-centered architecture, we always use parent's auth for database operations
      const parentId = authUser.uid;
      console.log('[MailService.markAsRead] Using parent authentication ID:', parentId);
      
      // Wrap ALL Firestore operations in NgZone
      return await this.ngZone.runOutsideAngular(async () => {
        console.log(`[MailService.markAsRead] Fetching document from 'messages/${messageId}'`);
        const messageRef = doc(this.firestore, 'messages', messageId);
        
        // Get the message to verify permissions
        const docSnap = await getDoc(messageRef);
        if (!docSnap.exists()) {
          console.error(`[MailService.markAsRead] Message with ID ${messageId} not found`);
          throw new Error('Message not found');
        }
        
        const messageData = docSnap.data() as FirestoreMessage;
        console.log('[MailService.markAsRead] Message data:', JSON.stringify({
          id: docSnap.id,
          parentId: messageData.parentId,
          senderId: messageData.senderId,
          actualSenderId: messageData.actualSenderId,
          recipientId: messageData.recipientId,
          read: messageData.read,
          location: messageData.location,
          ownerType: messageData.ownerType
        }));
        
        // Skip marking as read if this is a sender's copy (should already be marked as read)
        if (messageData.ownerType === 'sender') {
          console.log('[MailService.markAsRead] This is a sender copy of the message - already marked as read');
          return this.ngZone.run(() => {
            console.log('[MailService.markAsRead] No update needed for sender copy');
          });
        }
        
        // Verify this message belongs to the parent
        console.log(`[MailService.markAsRead] Checking parentId: document parentId=${messageData.parentId}, authenticated parentId=${parentId}`);
        if (messageData.parentId !== parentId) {
          console.error(`[MailService.markAsRead] PERMISSION ERROR: Document parentId=${messageData.parentId} does not match authenticated parentId=${parentId}`);
          throw new Error('You do not have permission to update this message');
        }
        console.log('[MailService.markAsRead] ParentId validation passed!');
        
        // In child mode, check if the recipient is the child
        if (childState.isChildMode && childId) {
          // Verify the recipient is the logged-in child
          if (messageData.recipientId !== childId) {
            console.error(`[MailService.markAsRead] Child ${childId} cannot mark message for recipient ${messageData.recipientId} as read`);
            throw new Error('Only the recipient can mark a message as read');
          }
          
          console.log(`[MailService.markAsRead] Child user (${childId}) marking message as read using parent auth (${parentId})`);
        } else {
          // In parent mode, verify the recipient is the parent
          if (messageData.recipientId !== parentId) {
            console.error(`[MailService.markAsRead] Parent cannot mark message for recipient ${messageData.recipientId} as read`);
            throw new Error('Only the recipient can mark a message as read');
          }
          
          console.log(`[MailService.markAsRead] Parent user (${parentId}) marking recipient copy as read`);
        }
        
        // Since parent is the authenticated user and document owner, update is allowed
        console.log(`[MailService.markAsRead] Updating document 'messages/${messageId}' to set read=true`);
        await updateDoc(messageRef, { read: true });
        
        return this.ngZone.run(() => {
          console.log('[MailService.markAsRead] Message marked as read successfully');
        });
      });
    } catch (error) {
      console.error('Error marking message as read:', error);
      throw error;
    }
  }

  // Update message location (move between inbox, archive, sent, trash)
  async updateMessageLocation(id: string, location: MessageLocation): Promise<void> {
    console.log(`[MailService.updateMessageLocation] Moving message ${id} to ${location}`);
    
    try {
      // Get child auth state to determine if we're in child mode
      const childState = await firstValueFrom(this.childAuthService.getAuthState());
      console.log('[MailService.updateMessageLocation] Child auth state:', JSON.stringify(childState));
      // Extract childId from the loggedInChild object if in child mode
      const childId = childState.isChildMode && childState.loggedInChild ? childState.loggedInChild.id : null;
      
      // Get authenticated user (always the parent in our architecture)
      const authUser = await this.authService.getCurrentUser();
      if (!authUser) {
        console.error('[MailService.updateMessageLocation] ERROR: No authenticated user found');
        throw new Error('No authenticated user found');
      }
      
      // In parent-centered architecture, we always use parent's auth for database operations
      const parentId = authUser.uid;
      console.log('[MailService.updateMessageLocation] Using parent authentication ID:', parentId);
      
      // Import needed functions from Firebase
      const { runTransaction, doc } = await import('firebase/firestore');
      
      // Create reference to the message document
      const messageRef = doc(this.firestore, 'messages', id);
      
      // Wrap ALL Firestore operations in NgZone
      await this.ngZone.runOutsideAngular(async () => {
        console.log(`[MailService.updateMessageLocation] Starting transaction for message ID ${id}`);
        
        try {
          // Use a transaction to ensure atomic read-write operations
          await runTransaction(this.firestore, async (transaction) => {
            console.log(`[MailService.updateMessageLocation] Reading document in transaction`);
            
            // Get the document in the transaction
            const docSnap = await transaction.get(messageRef);
            
            if (!docSnap.exists()) {
              console.error(`[MailService.updateMessageLocation] Message with ID ${id} not found`);
              throw new Error('Message not found');
            }
            
            const messageData = docSnap.data() as FirestoreMessage;
            console.log('[MailService.updateMessageLocation] Message data:', JSON.stringify(messageData));
            
            // Do permission checks based on who owns the message and if they're authorized to move it
            // Recipients can move their received messages between inbox/archive
            // Senders can move their sent messages between sent/archive
            // Parents can move any messages their children are involved in
            
            // Check child permissions
            if (childId) {
              console.log(`[MailService.updateMessageLocation] Child ID: ${childId} checking permissions`);
              // A child can only move messages they are directly involved in
              if (messageData.recipientId !== childId && messageData.senderId !== childId) {
                console.error('[MailService.updateMessageLocation] Permission denied: Child is not sender or recipient');
                throw new Error('Permission denied');
              }
              
              // Check that this move is valid based on ownership
              if (messageData.recipientId === childId && messageData.ownerType === 'recipient') {
                console.log('[MailService.updateMessageLocation] Recipient permission check passed');
                // Recipients can move between inbox, archive, and trash
                if (location !== 'inbox' && location !== 'archive' && location !== 'trash') {
                  console.error(`[MailService.updateMessageLocation] Invalid location '${location}' for recipient`);
                  throw new Error('Invalid location');
                }
              } else if (messageData.senderId === childId && messageData.ownerType === 'sender') {
                console.log('[MailService.updateMessageLocation] Sender permission check passed');
                // Senders can move between sent, archive, and trash
                if (location !== 'sent' && location !== 'archive' && location !== 'trash') {
                  console.error(`[MailService.updateMessageLocation] Invalid location '${location}' for sender`);
                  throw new Error('Invalid location');
                }
              } else {
                console.error('[MailService.updateMessageLocation] Permission denied for ownerType');
                throw new Error('Permission denied');
              }
            } else {
              // Parent permissions - can move any message they own
              if (messageData.parentId !== parentId) {
                console.error(`[MailService.updateMessageLocation] Parent ID mismatch: ${messageData.parentId} vs ${parentId}`);
                throw new Error('Permission denied');
              }
            }
            
            // Log detailed information about the message and what we're updating
            console.log(`[MailService.updateMessageLocation] Detailed message information:`);
            console.log(`- Document ID: ${id}`);
            console.log(`- Current location: ${messageData.location}`);
            console.log(`- New location: ${location}`);
            console.log(`- Owner type: ${messageData.ownerType}`);
            console.log(`- Parent ID: ${messageData.parentId}`);
            console.log(`- Sender ID: ${messageData.senderId}`);
            console.log(`- Recipient ID: ${messageData.recipientId}`);
            
            // IMPORTANT: With transactions, just update the minimal fields needed
            console.log(`[MailService.updateMessageLocation] Updating location to ${location} in transaction`);
            
            // Create a minimal update to avoid permission issues
            // When using transactions, we only need to specify the fields that are changing
            transaction.update(messageRef, {
              location: location,
              // Mark as read when moving messages
              read: true
            });
            
            console.log(`[MailService.updateMessageLocation] Transaction update submitted`);
          });
          
          console.log(`[MailService.updateMessageLocation] Successfully updated message location to ${location}`);
        } catch (error) {
          console.error('[MailService.updateMessageLocation] Firestore transaction error:', error);
          // Handle unknown error type safely
          if (error instanceof Error) {
            throw new Error(`Failed to update message location: ${error.message}`);
          } else {
            throw new Error('Failed to update message location: Unknown error');
          }
        }
      });
    } catch (error) {
      console.error(`[MailService.updateMessageLocation] Error updating message location:`, error);
      throw error;
    }
  }

  // Send a new message
  async sendMessage(message: Message): Promise<void> {
    console.log('[MailService.sendMessage] Sending message to Firestore:', JSON.stringify({
      subject: message.subject,
      recipientId: message.recipientId,
      senderId: message.senderId
    }));
    
    try {
      // Get child mode state
      const childState = await firstValueFrom(this.childAuthService.getAuthState());
      console.log('[MailService.sendMessage] Child auth state:', JSON.stringify({
        isChildMode: childState.isChildMode,
        childId: childState.loggedInChild?.id || 'none'
      }));

      // Get active child ID from our utility
      const childId = await this.firestoreUtils.getActiveChildId();
      
      // Get the authenticated parent user - this is crucial for security rules
      const authUser = await this.authService.getCurrentUser();
      if (!authUser) {
        console.error('[MailService.sendMessage] ERROR: No authenticated user found');
        throw new Error('No authenticated user found');
      }
      
      const parentId = authUser.uid; // This is the authenticated user (always the parent)
      console.log('[MailService.sendMessage] Using parent authentication ID:', parentId);
      
      // Add timestamp if not present (convert to Firestore Timestamp)
      const timestamp = message.timestamp ? Timestamp.fromDate(message.timestamp) : Timestamp.now();
      
      // Use FirestoreUtilsService to get the proper collection reference
      // This ensures we're using consistent security patterns across the app
      return await this.ngZone.runOutsideAngular(async () => {
        console.log('[MailService.sendMessage] Creating new documents in messages collection');
        
        try {
          // Get collection reference from firestoreUtils to ensure it's properly scoped
          const messagesCollection = collection(this.firestore, 'messages');
          
          // NEW ARCHITECTURE: Create TWO separate message documents (sender copy and recipient copy)
          // 1. Sender's copy - automatically in 'sent' folder and marked as read
          const senderMessageData: FirestoreMessage = {
            subject: message.subject,
            body: message.content,
            parentId: parentId,              // ALWAYS include parentId for security rules
            senderId: childState.isChildMode && childId ? childId : parentId,
            senderName: message.senderName || '',
            recipientId: message.recipientId,
            recipientName: message.recipientName || '',
            timestamp: timestamp,
            read: true,                      // Sender's copy is always marked as read
            location: 'sent',                // Sender's copy starts in the 'sent' folder
            actualSenderId: childState.isChildMode && childId ? childId : parentId,
            ownerType: 'sender'              // Indicates this is the sender's copy
          };
          
          // 2. Recipient's copy - automatically in 'inbox' folder and marked as unread
          const recipientMessageData: FirestoreMessage = {
            subject: message.subject,
            body: message.content,
            parentId: parentId,              // ALWAYS include parentId for security rules
            senderId: childState.isChildMode && childId ? childId : parentId,
            senderName: message.senderName || '',
            recipientId: message.recipientId,
            recipientName: message.recipientName || '',
            timestamp: timestamp,
            read: false,                     // Recipient's copy starts unread
            location: 'inbox',               // Recipient's copy starts in the 'inbox' folder
            actualSenderId: childState.isChildMode && childId ? childId : parentId,
            ownerType: 'recipient'           // Indicates this is the recipient's copy
          };
          
          console.log('[MailService.sendMessage] Creating sender copy with data:', JSON.stringify({
            parentId: senderMessageData.parentId,
            senderId: senderMessageData.senderId,
            recipientId: senderMessageData.recipientId,
            location: senderMessageData.location,
            read: senderMessageData.read,
            ownerType: senderMessageData.ownerType
          }));
          
          console.log('[MailService.sendMessage] Creating recipient copy with data:', JSON.stringify({
            parentId: recipientMessageData.parentId,
            senderId: recipientMessageData.senderId,
            recipientId: recipientMessageData.recipientId,
            location: recipientMessageData.location,
            read: recipientMessageData.read,
            ownerType: recipientMessageData.ownerType
          }));
          
          // Add both documents to Firestore
          const senderDocRef = await addDoc(messagesCollection, senderMessageData);
          const recipientDocRef = await addDoc(messagesCollection, recipientMessageData);
          
          console.log('[MailService.sendMessage] Message documents created with IDs:', {
            senderCopy: senderDocRef.id,
            recipientCopy: recipientDocRef.id
          });
          
          return this.ngZone.run(() => {
            console.log('[MailService.sendMessage] Message sent successfully with two copies');
          });
        } catch (error) {
          console.error('[MailService.sendMessage] Firestore error adding document:', error);
          if (error && typeof error === 'object' && 'code' in error && 'message' in error) {
            const firebaseError: any = error;
            console.error('[MailService.sendMessage] Error details:', {
              code: firebaseError.code,
              message: firebaseError.message
            });
            
            // More specific error handling for permission denied
            if (firebaseError.code === 'permission-denied') {
              console.error('[MailService.sendMessage] PERMISSION DENIED. Verify that parentId is set correctly and matches the authenticated user');
              throw new Error('Permission denied: You do not have permission to send messages. Verify you are properly authenticated.');
            }
          }
          throw error;
        }
      });
    } catch (error) {
      console.error('[MailService.sendMessage] Error sending message:', error);
      throw error;
    }
  }

  // Delete a message
  async deleteMessage(messageId: string): Promise<void> {
    console.log('[MailService.deleteMessage] Deleting message from Firestore:', messageId);
    
    try {
      // Get child mode state
      const childState = await firstValueFrom(this.childAuthService.getAuthState());
      console.log('[MailService.deleteMessage] Child auth state:', JSON.stringify({
        isChildMode: childState.isChildMode,
        childId: childState.loggedInChild?.id || 'none'
      }));
      
      // Get active child ID from our utility
      const childId = await this.firestoreUtils.getActiveChildId();
      
      // Get authenticated parent user
      const authUser = await this.authService.getCurrentUser();
      if (!authUser) {
        console.error('[MailService.deleteMessage] ERROR: No authenticated user found');
        throw new Error('No authenticated user found');
      }
      
      const parentId = authUser.uid;
      console.log('[MailService.deleteMessage] Using parent authentication ID:', parentId);
      
      // Wrap Firestore operations in NgZone to fix Firebase API outside Angular context warnings
      return await this.ngZone.runOutsideAngular(async () => {
        // First fetch the message to verify permissions
        console.log(`[MailService.deleteMessage] Fetching document from 'messages/${messageId}'`);
        const messageRef = doc(this.firestore, 'messages', messageId);
        
        try {
          // Check if the message belongs to this parent before deleting
          const docSnap = await getDoc(messageRef);
          
          if (!docSnap.exists()) {
            console.error(`[MailService.deleteMessage] Message with ID ${messageId} not found`);
            throw new Error('Message not found');
          }
          
          const messageData = docSnap.data() as FirestoreMessage;
          console.log('[MailService.deleteMessage] Message data:', JSON.stringify({
            id: docSnap.id,
            parentId: messageData.parentId,
            senderId: messageData.senderId,
            actualSenderId: messageData.actualSenderId,
            recipientId: messageData.recipientId
          }));
          
          // Verify this message belongs to the parent
          console.log(`[MailService.deleteMessage] Checking parentId: document parentId=${messageData.parentId}, authenticated parentId=${parentId}`);
          if (messageData.parentId !== parentId) {
            console.error(`[MailService.deleteMessage] PERMISSION ERROR: Document parentId=${messageData.parentId} does not match authenticated parentId=${parentId}`);
            throw new Error('You do not have permission to delete this message');
          }
          console.log('[MailService.deleteMessage] ParentId validation passed!');
          
          // Apply additional client-side permission check in child mode
          if (childState.isChildMode && childId) {
            // For child user, they should only be able to delete messages they sent or received
            const hasPermission = 
              messageData.recipientId === childId || 
              messageData.actualSenderId === childId;
              
            if (!hasPermission) {
              console.error(`[MailService.deleteMessage] Child user ${childId} cannot delete message for recipientId=${messageData.recipientId}, actualSenderId=${messageData.actualSenderId}`);
              throw new Error('You do not have permission to delete this message');
            }
            
            console.log(`[MailService.deleteMessage] Child user (${childId}) has permission to delete this message`);
          }
          
          // Only after permission check, proceed with delete
          console.log(`[MailService.deleteMessage] Deleting document 'messages/${messageId}'`);
          await deleteDoc(messageRef);
          
          // Run UI updates back in Angular zone
          return this.ngZone.run(() => {
            console.log('[MailService.deleteMessage] Message deleted successfully');
          });
        } catch (error) {
          console.error('[MailService.deleteMessage] Firestore error:', error);
          if (error && typeof error === 'object' && 'code' in error && 'message' in error) {
            console.error('[MailService.deleteMessage] Error code:', error.code);
            console.error('[MailService.deleteMessage] Error message:', error.message);
          }
          throw error;
        }
      });
    } catch (error) {
      console.error('[MailService.deleteMessage] Error deleting message:', error);
      throw error;
    }
  }

  // Helper to get current user ID (either parent or child)
  private getCurrentUserId(): Observable<string | null> {
    return this.childAuthService.getAuthState().pipe(
      switchMap(state => {
        // First check if a child is logged in
        if (state.isChildMode && state.loggedInChild) {
          console.log('Child user is logged in:', state.loggedInChild.id);
          return of(state.loggedInChild.id);
        }
        
        // Otherwise check if a parent/adult is logged in
        return this.authService.currentUser$.pipe(
          map(user => {
            if (user) {
              console.log('Parent/adult user is logged in:', user.uid);
              return user.uid;
            }
            return null;
          })
        );
      })
    );
  }

  // Mock implementation for development purposes
  private getMockMessages(userId: string): Message[] {
    return [
      {
        id: 'msg1',
        senderId: 'parent1',
        senderName: 'Mom',
        recipientId: userId,
        subject: 'Great job!',
        content: 'You did a wonderful job on your chores today!',
        timestamp: new Date(),
        read: false
      },
      {
        id: 'msg2',
        senderId: 'child2',
        senderName: 'Sister',
        recipientId: userId,
        subject: 'Game this weekend?',
        content: 'Do you want to play board games this weekend?',
        timestamp: new Date(Date.now() - 86400000), // 1 day ago
        read: true
      }
    ];
  }
}
